import { styled } from "@mui/material";
import { Quote } from "lucide-react";
import { tokens } from "@styles/tokens";

interface TestimonialItem {
  quote: string;
  name: string;
  role: string;
  initials: string;
}

// Temporary testimonials so we can see the UI. Swap for real ones later; to make
// this a rotating carousel, drive `current` from an interval/state.
const TESTIMONIALS: TestimonialItem[] = [
  {
    quote:
      "Skuey has transformed how we manage inventory across all our warehouses.",
    name: "James Carter",
    role: "Operations Manager, Acme Corp",
    initials: "JC",
  },
  {
    quote:
      "We cut our stock counts from days to hours — the team actually enjoys it now.",
    name: "Priya Nair",
    role: "Head of Ops, Northwind Goods",
    initials: "PN",
  },
  {
    quote:
      "Finally one source of truth for every location. Setup was painless.",
    name: "Diego Romero",
    role: "Founder, Loma Supply",
    initials: "DR",
  },
];

/** Static testimonial card shown on the desktop brand panel. */
export function Testimonial() {
  const current = 0;
  const item = TESTIMONIALS[current];

  return (
    <Card>
      <QuoteMark>
        <Quote size={18} strokeWidth={1.75} />
      </QuoteMark>
      <Text>{item.quote}</Text>
      <Person>
        <Avatar>{item.initials}</Avatar>
        <div>
          <Name>{item.name}</Name>
          <Role>{item.role}</Role>
        </div>
      </Person>
      <Dots>
        {TESTIMONIALS.map((t, i) => (
          <Dot key={t.name} $active={i === current} />
        ))}
      </Dots>
    </Card>
  );
}

const Card = styled("figure")(({ theme }) => ({
  margin: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(3),
  padding: tokens.space(5),
  borderRadius: tokens.radius.lg,
  backgroundColor: theme.skuey.surface,
  border: `1px solid ${theme.skuey.border}`,
  boxShadow: tokens.shadow.sm,
}));

const QuoteMark = styled("span")(({ theme }) => ({
  display: "flex",
  color: theme.skuey.brand[600],
}));

const Text = styled("blockquote")(({ theme }) => ({
  margin: 0,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.body[0],
  lineHeight: `${tokens.font.size.body[1]}px`,
  color: theme.skuey.body,
}));

const Person = styled("figcaption")({
  display: "flex",
  alignItems: "center",
  gap: tokens.space(3),
});

const Avatar = styled("span")(({ theme }) => ({
  display: "grid",
  placeContent: "center",
  width: 38,
  height: 38,
  flexShrink: 0,
  borderRadius: tokens.radius.full,
  backgroundColor: theme.skuey.brand[600],
  color: theme.skuey.white,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.tiny[0],
  fontWeight: tokens.font.weight.semibold,
}));

const Name = styled("div")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.ink,
}));

const Role = styled("div")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.micro[0],
  lineHeight: `${tokens.font.size.micro[1]}px`,
  color: theme.skuey.muted,
}));

const Dots = styled("div")({
  display: "flex",
  gap: tokens.space(1.5),
});

const Dot = styled("span", {
  shouldForwardProp: (p) => p !== "$active",
})<{ $active: boolean }>(({ theme, $active }) => ({
  width: $active ? 18 : 6,
  height: 6,
  borderRadius: tokens.radius.full,
  backgroundColor: $active ? theme.skuey.brand[600] : theme.skuey.borderStrong,
  transition: "width .2s ease, background-color .2s ease",
}));
